import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import iconStandalone from "../../../assets/images/icon-standalone-system.png";
import iconWithExisting from "../../../assets/images/icon-integration.png";
import SearchableDropdown from "../components/SearchableDropdown";

// Common existing systems/ERP systems
const EXISTING_SYSTEMS = [
  'Epicor iScala',
  'Microsoft Dynamics 365 Business Central',
  'Microsoft Dynamics NAV',
  'Microsoft Dynamics AX',
  'Microsoft Dynamics GP',
  'Microsoft Dynamics CRM',
  'SAP',
  'SAP Business One',
  'SAP ERP',
  'Oracle ERP Cloud',
  'Oracle NetSuite',
  'Oracle E-Business Suite',
  'Visma Horizon',
  'Visma Business',
  'Visma Global',
  'Sage',
  'Sage X3',
  'Sage 300',
  'Sage Intacct',
  'QuickBooks',
  'QuickBooks Enterprise',
  'Xero',
  'MYOB',
  'Odoo',
  'Zoho',
  'Infor',
  'IFS',
  'Acumatica',
  'Workday',
  'Unit4',
  'Exact',
  'Mamut',
  'Other'
].sort();

const UsageTypePage = ({ data, onUpdate }) => {
  const [usageType, setUsageType] = useState(data.usageType || "");
  const [existingSystem, setExistingSystem] = useState(data.existingSystem || "");
  const [fieldErrors, setFieldErrors] = useState({
    usageType: '',
    existingSystem: ''
  });

  // Update parent component when data changes
  useEffect(() => {
    onUpdate({
      usageType,
      existingSystem
    });
  }, [usageType, existingSystem]);

  // Handle usage type selection
  const handleUsageTypeChange = (type) => {
    setUsageType(type);
    scrollTo('form-content');
    setFieldErrors(prev => ({
      ...prev,
      usageType: ''
    }));

    // Clear existing system if switching to standalone
    if (type === 'standalone') {
      setExistingSystem('');
      setFieldErrors(prev => ({
        ...prev,
        existingSystem: ''
      }));
    }
  };

  // Handle existing system input
  const handleExistingSystemChange = (e) => {
    const value = e.target.value || '';
    setExistingSystem(value);

    // Validate input
    if (value.trim().length === 0) {
      setFieldErrors(prev => ({
        ...prev,
        existingSystem: 'Please enter the name of your existing system'
      }));
    } else if (value.trim().length < 2) {
      setFieldErrors(prev => ({
        ...prev,
        existingSystem: 'System name must be at least 2 characters long'
      }));
    } else {
      setFieldErrors(prev => ({
        ...prev,
        existingSystem: ''
      }));
    }
  };

  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      // Scroll to the element with some offset from the top
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
  };


  return (
    <div className="wizard-page usage-type-page">
      <div className="page-header">
        <h2>How are you planning to use CashManager?</h2>
        {/* <p>This helps us understand your setup and provide the best configuration for your needs.</p> */}
      </div>

      <div className="form-content" id="form-content">
        <div className="form-group">
          <div className="radio-options">
            <div className="radio-option">
              <input
                type="radio"
                id="standalone"
                name="usageType"
                value="standalone"
                checked={usageType === 'standalone'}
                onChange={() => handleUsageTypeChange('standalone')}
                className="radio-input"
              />
              <label htmlFor="standalone" className="radio-label">
                <div className="option-content">
                  <div className="option-icon"><img src={iconStandalone} /></div>
                  <div className="option-text">
                    <h3>I will use CashManager as a standalone system</h3>
                    <p>CashManager will be your primary business management system</p>
                  </div>
                </div>
              </label>
            </div>

            <div className="radio-option">
              <input
                type="radio"
                id="with-existing"
                name="usageType"
                value="with-existing"
                checked={usageType === 'with-existing'}
                onChange={() => handleUsageTypeChange('with-existing')}
                className="radio-input"
              />
              <label htmlFor="with-existing" className="radio-label">
                <div className="option-content">
                  <div className="option-icon"><img src={iconWithExisting} /></div>
                  <div className="option-text">
                    <h3>I will use CashManager with my existing system</h3>
                    <p>CashManager will enhance the functionality of your current business management system</p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {fieldErrors.usageType && (
            <span className="field-error">{fieldErrors.usageType}</span>
          )}
        </div>

        {usageType === 'with-existing' && (
          <div className="form-group">
            <label htmlFor="existingSystem" className="form-label">
              What is the name of your existing system?
            </label>
            <SearchableDropdown
              options={EXISTING_SYSTEMS.map(system => ({ value: system, label: system }))}
              value={existingSystem}
              onChange={handleExistingSystemChange}
              placeholder="e.g., SAP, Oracle, Microsoft Dynamics, etc."
              className={fieldErrors.existingSystem ? 'error' : ''}
              error={!!fieldErrors.existingSystem}
              name="existingSystem"
              allowFreeText={true}
            />
            {fieldErrors.existingSystem && (
              <span className="field-error">{fieldErrors.existingSystem}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

UsageTypePage.propTypes = {
  data: PropTypes.shape({
    usageType: PropTypes.string,
    existingSystem: PropTypes.string
  }).isRequired,
  onUpdate: PropTypes.func.isRequired
};

export default UsageTypePage;
